import {
	Card,
	Checkbox,
	Form,
	FormInstance,
	Grid,
	Tabs,
	Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { CategoryStepUIConfig } from '../../../shared/CategoryStepUIConfig';
import {
	Answers,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { MultipleChoiceQuestionUIConfig } from '../../../shared/MultipleChoiceQuestionUIConfig';
import { ApplicantAnswerToQuestion } from '../ApplicantAnswerToQuestion';
import { CategoryAnswerType } from '../../applicant/NewApplicationWizard/CategoryAnswerType';
import { MultipleChoiceQuestion } from '../../applicant/NewApplicationWizard/MultipleChoiceQuestion';
import { PredefinedReasonCategory } from '../../../shared/Constants';
import { PreWorkRecommendation } from './PreWorkRecommendation';
import { PreworkReviewAction } from '../../../shared/PreWorkServiceTypes';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface CategoryStepProps {
	uiConfig: CategoryStepUIConfig;
	mandatoryQuestionsAndAnswersForm: FormInstance;
	answers?: Answers;
	mentorAnswers?: Answers;
	showApplicantAnswers?: boolean;
	updateFieldValue: (questionId: number, value: number) => void;
	isPreworkApproved?: boolean;
	approverPreworkRecommendation?: PreworkReviewAction;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description?: string | null;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-lg text-primary-blue">
				{heading}
			</Text>
			{description && (
				<Text
					className="font-sans font-normal text-sm mb-[8px]"
					type="secondary"
				>
					{description}
				</Text>
			)}
		</div>
	);
};

export const CategoryStep = ({
	uiConfig: {
		title,
		subTitle,
		questionsToAskInPreworkMentorInterview,
		questions,
	},
	mandatoryQuestionsAndAnswersForm,
	answers,
	mentorAnswers,
	showApplicantAnswers = true,
	updateFieldValue,
	isPreworkApproved = false,
	approverPreworkRecommendation,
}: CategoryStepProps) => {
	const { t } = useTranslation();
	const { selectedRole } = useUserSession();
	const isApprover = selectedRole === UserRole.APPROVER;
	const canDisplayComments =
		selectedRole === UserRole.APPROVER || selectedRole === UserRole.SNP_ADMIN;
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const formValues = Form.useWatch<CategoryAnswerType>(
		[],
		mandatoryQuestionsAndAnswersForm
	);

	const allQuestions = questions.map(
		(question: MultipleChoiceQuestionUIConfig) => {
			const value = answers?.questionsAndAnswers.find(
				(answer) => answer.questionId === question.questionId
			);
			return (
				<div key={question.questionId} className="my-[8px]">
					<ApplicantAnswerToQuestion
						uiConfig={question}
						value={value}
						showBG={isMobile ? true : false}
					/>
				</div>
			);
		}
	);

	const mandatoryQuestions =
		questionsToAskInPreworkMentorInterview.mandatoryQuestionsToAsk.map(
			(question: MultipleChoiceQuestionUIConfig) => (
				<Card
					key={question.questionId}
					className="my-5 !border !rounded-lg !border-gray-250 !bg-gray-100 p-0"
				>
					<div key={question.questionId} className="my-[8px]">
						<MultipleChoiceQuestion
							uiConfig={question}
							value={{
								selectedOption:
									formValues?.[question.questionId]?.selectedOption ??
									undefined,
							}}
							updateFieldValue={updateFieldValue}
						/>
					</div>
				</Card>
			)
		);

	const mandatoryQuestionsAfterApproval =
		questionsToAskInPreworkMentorInterview.mandatoryQuestionsToAsk.map(
			(question: MultipleChoiceQuestionUIConfig) => {
				const value = mentorAnswers?.questionsAndAnswers.find(
					(answer) => answer.questionId === question.questionId
				);
				return (
					<div key={question.questionId} className="my-[8px]">
						<ApplicantAnswerToQuestion
							uiConfig={question}
							value={value}
							showBG={isMobile ? true : false}
						/>
					</div>
				);
			}
		);

	return (
		<div className="flex flex-col h-full min-h-0 max-w-[600px]">
			<Form
				name="mandatoryQuestionsAndAnswersForm"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={mandatoryQuestionsAndAnswersForm}
			>
				<SectionHeader heading={t(title)} description={t(subTitle)} />
				<Tabs
					defaultActiveKey="1"
					centered
					tabBarStyle={{ fontWeight: 'bold' }}
				>
					<Tabs.TabPane tab={t('preworkMentor')} key="1">
						{isPreworkApproved || isApprover ? (
							<>{mandatoryQuestionsAfterApproval}</>
						) : (
							<>
								{mandatoryQuestions}
								<Form.Item
									name="iConfirmAllTheAboveInfoIsTrue"
									valuePropName="checked"
								>
									<Checkbox className="font-bold text-black-100 [&_.ant-checkbox-inner]:border-black-100 [&_.ant-checkbox-inner]:border-2">
										{t('iConfirmAllTheAboveInfoIsTrue')}
									</Checkbox>
								</Form.Item>
							</>
						)}
						{isApprover && (
							<div className="flex flex-row items-start">
								<div className="flex flex-col w-full">
									<SectionHeader heading={t('feedbackFromApprover')} />
									<PreWorkRecommendation
										commentsMandatory
										commentsLabel={'approverComments'}
										commentsPlaceholder="commentsAreMandatory"
										form={mandatoryQuestionsAndAnswersForm}
										category={PredefinedReasonCategory.APPROVER_REJECT}
										displayComments={canDisplayComments}
										disableReview={!canDisplayComments}
										disabled={
											approverPreworkRecommendation !==
											PreworkReviewAction.PENDING
												? true
												: false
										}
									/>
								</div>
							</div>
						)}
					</Tabs.TabPane>
					{showApplicantAnswers && (
						<Tabs.TabPane tab={t('candidate')} key="2">
							{allQuestions}
						</Tabs.TabPane>
					)}
				</Tabs>
			</Form>
		</div>
	);
};
